/**
 * recipients.js
 *
 * Helpers that resolve TherapieMaps reference data into notification
 * recipients for a given case context (region or clinic). Shared by the
 * Comments composer today; the case-submission and other backend-triggered
 * notification flows should consume the same lookups when they're wired up.
 *
 * All inputs are case-insensitive and tolerant of missing fields.
 */

/**
 * HoROs whose region matches `region`.
 * Returns [] if region is falsy or TM data isn't loaded.
 *
 * @param {object|null} tmData — return value of getTherapieMapsData()
 * @param {string|null} region — exact region string from a case (matches
 *   th-horo.json's customSecurityAttributes.LocationSorting.Region, which is
 *   the same format as th-clinics.json's `region` field)
 * @returns {Array<{ mail: string, displayName?: string, region: string|null }>}
 */
export function findHoROsForRegion(tmData, region) {
  if (!tmData?.horoByMail || !region) return [];
  const wanted = region.trim().toLowerCase();
  // Primary (region) OR secondary (regionSec) — must mirror the API's
  // horosForRegion in notificationRecipients.js so the rule preview matches
  // what the engine actually sends.
  return Object.values(tmData.horoByMail).filter(h =>
    [h.region, h.regionSec].some(r => (r || '').trim().toLowerCase() === wanted)
  );
}

/**
 * Clinic Managers responsible for the named clinic.
 * Multiple CMs may share a clinic — returns all of them.
 *
 * @param {object|null} tmData
 * @param {string|null} clinicName — exact clinic name from a case (matches
 *   th-clinics.json's `name` field)
 * @returns {Array<object>} CM records with their clinicObjects
 */
export function findCMsForClinic(tmData, clinicName) {
  if (!tmData?.cmByMail || !clinicName) return [];
  const wanted = clinicName.trim().toLowerCase();
  return Object.values(tmData.cmByMail).filter(cm =>
    (cm.clinicObjects || []).some(c => (c.name || '').trim().toLowerCase() === wanted)
  );
}

/**
 * Convenience: return a single representative email for a notification slot.
 * If multiple HoROs/CMs match, the first is returned (callers wanting *all*
 * should use findHoROsForRegion / findCMsForClinic directly and `.map(x => x.mail)`).
 */
export function pickHoROEmail(tmData, region) {
  return findHoROsForRegion(tmData, region)[0]?.mail || null;
}
export function pickCMEmail(tmData, clinicName) {
  return findCMsForClinic(tmData, clinicName)[0]?.mail || null;
}
